import React, { useEffect, useState } from "react"
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import PasswordField from "./PasswordField"
import api from "../utils/api"

export function CreateEventAdminForm() {
  const navigate = useNavigate()

  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [selectedEvent, setSelectedEvent] = useState("")
  const [events, setEvents] = useState<{ name: string; id: string }[]>([])
  const [errorMessage, setErrorMessage] = useState("")
  const [usernameError, setUsernameError] = useState("")
  const [eventAdminUsernames, setEventAdminUsernames] = useState<string[]>([])

  const fetchEvents = async (
    url: string,
    setter: React.Dispatch<React.SetStateAction<any>>
  ) => {
    try {
      const response = await api.get(url)
      const data = response.data
      if (Array.isArray(data)) {
        setter(data)
      } else {
        console.warn("Received non-array data:", data)
      }
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error)
      setter([])
    }
  }

  //fetches all event admins
  const fetchEventAdminUsernames = async () => {
    try {
      const response = await api.get("/admin?type=eventAdmin")
      const data = response.data
      setEventAdminUsernames(data)
    } catch (error) {
      console.error("Error fetching event admin usernames:", error)
    }
  }

  useEffect(() => {
    fetchEvents("/admin/events", setEvents)
    fetchEventAdminUsernames()
  }, [])

  useEffect(() => {
    const usernamesArray = Array.isArray(eventAdminUsernames)
      ? eventAdminUsernames
      : []
    const exists = usernamesArray.includes(username.trim())
    setUsernameError(exists ? "This username is already taken." : "")
  }, [username, eventAdminUsernames])

  const handleSubmit = async () => {
    if (phone && !isValidPhone(phone)) return
    if (!allFieldsFilled()) {
      setErrorMessage("All fields are required!")
      return
    }

    if (usernameError) {
      setErrorMessage("This username is already taken.")
      return
    }

    try {
      const oneWeekFromNow = new Date()
      oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7)

      await api.post("/admin/event-admin", {
        username,
        password,
        name,
        phone,
        type: "eventAdmin",
        eventId: selectedEvent,
        expirationDate: oneWeekFromNow.toISOString(),
      })

      navigate("/admin/dashboard", {
        state: { notification: "Event admin successfully created!" },
      })
    } catch (error) {
      const message =
        error.response?.data?.message || "An unexpected error occurred"

      // Specific check for a 400 status code and a 'Username already exists' message
      setErrorMessage(
        error.response?.status === 400 && message === "Username already exists"
          ? "Username already exists"
          : message
      )
      console.error("Error creating event admin:", error)
    }
  }

  const isValidPhone = (phone: string) => /^(\d{10})$/.test(phone)
  const allFieldsFilled = () =>
    username && name && phone && selectedEvent && password

  return (
    <>
      <Typography variant="h6" m={4}>
        Create An Event Admin
      </Typography>

      {/* Username */}
      <TextField
        label="Username"
        variant="outlined"
        fullWidth
        margin="normal"
        value={username}
        required
        onChange={(e) => {
          setUsername(e.target.value.replace(/\s/g, ""))
        }}
        error={!!usernameError}
        helperText={usernameError}
      />

      {/* Name */}
      <TextField
        label="Name"
        variant="outlined"
        fullWidth
        margin="normal"
        value={name}
        required
        onChange={(e) => setName(e.target.value)}
      />

      {/* Phone */}
      <TextField
        label="Phone"
        variant="outlined"
        fullWidth
        margin="normal"
        value={phone}
        required
        onChange={(e) => setPhone(e.target.value)}
        error={!!(phone && !/^(\d{10})$/.test(phone))}
        helperText={
          phone && !/^(\d{10})$/.test(phone)
            ? "Please enter a 10-digit number."
            : ""
        }
        inputProps={{
          pattern: "\\d{10}",
        }}
      />

      {/* Password */}
      <PasswordField
        password={password}
        setPassword={setPassword}
        generatePassword={generatePassword}
      />

      {/* Events Dropdown */}
      <FormControl fullWidth margin="normal">
        <InputLabel required id="event-select-label">
          Event
        </InputLabel>
        <Select
          label="Event"
          labelId="event-select-label"
          id="event-select"
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          required
        >
          {events.map((event) => (
            <MenuItem key={event.id} value={event.id}>
              {event.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Submit Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        sx={{ mt: 3 }}
      >
        Create
      </Button>

      {/* Snackbar */}
      {errorMessage && (
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={() => setErrorMessage("")} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}

function generatePassword() {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"
  const numbers = "0123456789"
  const specialChars = "!@#$%^&*"

  // Randomly pick one character from each set
  const randomChar = chars.charAt(Math.floor(Math.random() * chars.length))
  const randomNumber = numbers.charAt(
    Math.floor(Math.random() * numbers.length)
  )
  const randomSpecialChar = specialChars.charAt(
    Math.floor(Math.random() * specialChars.length)
  )

  // Random number between 3 and 10
  const randomLength = Math.floor(Math.random() * 8) + 3

  // Generate the rest of the password
  const randomChars = Array(randomLength)
    .fill(chars + numbers + specialChars)
    .map((set) => set.charAt(Math.floor(Math.random() * set.length)))
    .join("")

  // Combine everything
  return randomChar + randomNumber + randomSpecialChar + randomChars
}
